import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Error desconocido";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

export default function Login() {
  const auth = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await auth.login(email, password);
      toast.success("Bienvenido a Ovamx");
      const next = loc.state?.from || "/";
      nav(next, { replace: true });
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-cream text-ink flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size={56} className="mx-auto" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Iniciar sesión</p>
          <h1 className="mt-2 font-serif text-5xl tracking-tighter leading-none">Bienvenido<br/>de vuelta.</h1>
          <p className="mt-3 text-sm text-ink-mid">Tu tiempo tiene valor.</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field icon={Mail} testid="login-email" type="email" placeholder="email@ovamx.com"
            value={email} onChange={setEmail} required autoFocus />
          <Field icon={Lock} testid="login-password" type="password" placeholder="contraseña"
            value={password} onChange={setPassword} required />

          {err && (
            <div data-testid="login-error" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <button type="submit" disabled={busy}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-3 hover:bg-coral transition disabled:opacity-50">
            {busy ? "Ingresando…" : <>Ingresar <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-ink-mid space-y-1.5">
          <p>
            ¿Sin cuenta? <Link to="/register" data-testid="login-to-register" className="text-coral hover:underline">Regístrate</Link>
          </p>
          <p>
            <Link to="/forgot-password" data-testid="login-forgot" className="hover:text-coral">¿Olvidaste tu contraseña?</Link>
          </p>
          <p className="pt-3">
            <Link to="/" className="text-ink-mid hover:text-coral text-[11px] font-mono uppercase tracking-wider">← Seguir como invitado</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
      <input
        data-testid={testid}
        {...props}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full bg-cream-card border hairline rounded-2xl pl-11 pr-4 py-3 text-sm outline-none focus:border-coral"
      />
    </div>
  );
}
